import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, switchMap, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG } from '../config';

interface UploadUrlResponse {
  uploadUrl: string;
  expiresAt: string;
}

/**
 * Service for managing organization logo and cover image uploads.
 *
 * Upload flow (3 steps per asset):
 * 1. `getLogoUploadUrl()` / `getCoverUploadUrl()` — request a signed GCS URL from the backend
 * 2. PUT the raw file bytes directly to GCS using the signed URL
 *    (no Authorization header needed — GCS signed URLs are self-authenticating;
 *     the auth interceptor already skips non-API-base URLs)
 * 3. `saveLogo()` / `saveCover()` — notify the backend of the final public URL
 *
 * The `uploadLogo()` / `uploadCover()` helpers orchestrate all 3 steps and emit
 * the final public URL.
 *
 * Server-enforced constraints:
 * | Asset | Max size | Allowed MIME types                       |
 * |-------|----------|------------------------------------------|
 * | Logo  | 2 MB     | image/png, image/jpeg, image/webp        |
 * | Cover | 5 MB     | image/jpeg, image/webp                   |
 *
 * Client-side validation (before calling these methods):
 * | Asset | Aspect ratio | Max dimensions  |
 * |-------|-------------|-----------------|
 * | Logo  | 1:1 (square) | 512 × 512 px   |
 * | Cover | 16:9 (±5%)  | 1920 × 1080 px  |
 */
@Injectable({
  providedIn: 'root',
})
export class OrgBrandingService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl: string;

  constructor() {
    const config = inject(USER_MANAGEMENT_API_CONFIG);
    this._apiUrl = config.apiBaseUrl;
  }

  // ---------------------------------------------------------------------------
  // Logo
  // ---------------------------------------------------------------------------

  getLogoUploadUrl(
    orgId: string,
    mimeType: string,
    fileSizeBytes: number,
  ): Observable<UploadUrlResponse> {
    return this._http
      .post<{ success: boolean; data: UploadUrlResponse }>(
        `${this._apiUrl}/api/organizations/${orgId}/logo/upload-url`,
        { mimeType, fileSizeBytes },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getLogoUploadUrl')),
      );
  }

  saveLogo(
    orgId: string,
    url: string,
    storagePath: string,
    fileSizeBytes: number,
  ): Observable<{ logoUrl: string }> {
    return this._http
      .patch<{ success: boolean; data: { logoUrl: string } }>(
        `${this._apiUrl}/api/organizations/${orgId}/logo`,
        { url, storagePath, fileSizeBytes },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('saveLogo')),
      );
  }

  /**
   * Full 3-step logo upload orchestration.
   * Emits the final public GCS URL on success.
   */
  uploadLogo(orgId: string, file: File): Observable<string> {
    return this.getLogoUploadUrl(orgId, file.type, file.size).pipe(
      switchMap(({ uploadUrl }) =>
        this._putToGcs(uploadUrl, file).pipe(
          switchMap(() => {
            const storagePath = new URL(uploadUrl).pathname.slice(1); // strip leading /
            return this.saveLogo(orgId, this._publicUrl(uploadUrl), storagePath, file.size);
          }),
          map(res => res.logoUrl),
        ),
      ),
      catchError(this._handleError('uploadLogo')),
    );
  }

  // ---------------------------------------------------------------------------
  // Cover image
  // ---------------------------------------------------------------------------

  getCoverUploadUrl(
    orgId: string,
    mimeType: string,
    fileSizeBytes: number,
  ): Observable<UploadUrlResponse> {
    return this._http
      .post<{ success: boolean; data: UploadUrlResponse }>(
        `${this._apiUrl}/api/organizations/${orgId}/cover/upload-url`,
        { mimeType, fileSizeBytes },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getCoverUploadUrl')),
      );
  }

  saveCover(
    orgId: string,
    url: string,
    storagePath: string,
    fileSizeBytes: number,
  ): Observable<{ coverImageUrl: string }> {
    return this._http
      .patch<{ success: boolean; data: { coverImageUrl: string } }>(
        `${this._apiUrl}/api/organizations/${orgId}/cover`,
        { url, storagePath, fileSizeBytes },
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('saveCover')),
      );
  }

  /**
   * Full 3-step cover upload orchestration.
   * Emits the final public GCS URL on success.
   */
  uploadCover(orgId: string, file: File): Observable<string> {
    return this.getCoverUploadUrl(orgId, file.type, file.size).pipe(
      switchMap(({ uploadUrl }) =>
        this._putToGcs(uploadUrl, file).pipe(
          switchMap(() => {
            const storagePath = new URL(uploadUrl).pathname.slice(1);
            return this.saveCover(orgId, this._publicUrl(uploadUrl), storagePath, file.size);
          }),
          map(res => res.coverImageUrl),
        ),
      ),
      catchError(this._handleError('uploadCover')),
    );
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * PUT raw file bytes to a GCS signed URL.
   * GCS signed URLs are self-authenticating — no Authorization header required.
   * The app-level auth interceptor skips this request because the URL does not
   * start with the CMS API base URL.
   */
  private _putToGcs(signedUrl: string, file: File): Observable<void> {
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this._http.put<void>(signedUrl, file, { headers }).pipe(
      catchError(this._handleError('putToGcs')),
    );
  }

  /**
   * Derives the public GCS URL from a signed upload URL by stripping
   * query parameters (the signature, expiry, etc.).
   */
  private _publicUrl(signedUrl: string): string {
    const url = new URL(signedUrl);
    return `${url.protocol}//${url.host}${url.pathname}`;
  }

  private _handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      console.error(`[OrgBrandingService] ${operation} failed:`, error);
      return throwError(() => error);
    };
  }
}
